import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { Subject, takeUntil, tap } from 'rxjs';

import { OrderPostedApiService } from 'v2/modules/shared/services/api/order/order-posted-api.service';
import { AgGridValueFormatter } from 'v2/modules/shared/util/ag-grid-value-formatter.utils';

@Component({
  selector: 'lum-refund-return-orders-ag-table',
  standalone: true,
  imports: [CommonModule, AgGridModule],
  templateUrl: './order-refund-return-orders-ag-table.component.html',
  styleUrls: ['./order-refund-return-orders-ag-table.component.scss'],
})
export class OrderRefundReturnOrdersAgTableComponent implements OnInit, OnDestroy {
  @Input() parentSalesOrderId: number;

  // Combined array of refund and return orders with type property
  columnDefs: ColDef[];
  gridOptions: GridOptions;
  refundReturnOrders: any[] = [];
  loading: boolean = false;

  private gridApi: GridApi;
  private onDestroy$ = new Subject<void>();

  constructor(
    private orderPostedApiService: OrderPostedApiService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initColumnDefs();
    this.initGridOptions();
    this.fetchRefundReturnOrders();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.loadData();
  }

  private initColumnDefs(): void {
    this.columnDefs = [
      {
        cellClass: 'lum-ag-expand-cell',
        cellRenderer: 'agGroupCellRenderer',
        filter: false,
        floatingFilter: false,
        headerClass: 'lum-ag-expand-cell lum-ag-expand-cell--header',
        lockPinned: true,
        lockPosition: true,
        maxWidth: 40,
        minWidth: 40,
        pinned: 'left',
        resizable: false,
        sortable: false,
        suppressColumnsToolPanel: true,
        suppressMenu: true,
        suppressMovable: true,
        suppressNavigable: true,
        width: 40,
      },
      {
        headerName: 'Order ID',
        field: 'id',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Type',
        field: 'type',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Refund', 'Return'],
        },
        cellRenderer: (params) => {
          const type = params.value;
          const isRefund = type === 'Refund';
          const color = isRefund ? '#dc3545' : '#fd7e14'; // Red for refund, orange for return

          return `<span style="color: ${color}; font-weight: bold;">${type}</span>`;
        },
      },
      {
        headerName: 'Order Date',
        field: 'orderDate',
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals'],
          defaultOption: 'equals',
          suppressAndOrCondition: true,
        },
        valueFormatter: AgGridValueFormatter.DATE,
      },
      {
        headerName: 'Order Status',
        field: 'orderStatus',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Source',
        field: 'source',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Total Quantity',
        field: 'totalOrderQuantity',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: ['equals'],
          defaultOption: 'equals',
          suppressAndOrCondition: true,
        },
        valueFormatter: AgGridValueFormatter.DECIMAL_PLACES,
      },
      {
        headerName: 'Order Total',
        field: 'orderTotal',
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: ['equals'],
          defaultOption: 'equals',
          suppressAndOrCondition: true,
        },
        valueFormatter: AgGridValueFormatter.CURRENCY,
      },
    ];
  }

  private initGridOptions(): void {
    this.gridOptions = {
      rowSelection: 'multiple',
      pagination: true,
      paginationPageSize: 100,
      suppressScrollOnNewData: true,
      defaultColDef: {
        sortable: true,
        flex: 1,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 100,
      },
      masterDetail: true,
      detailCellRendererParams: {
        detailGridOptions: {
          columnDefs: this.getDetailColumnDefs(),
          defaultColDef: {
            flex: 1,
            resizable: false,
            sortable: false,
            suppressMenu: true,
          },
        },
        getDetailRowData: (params) => {
          if (params.data.orderItems && params.data.orderItems.length > 0) {
            params.successCallback(params.data.orderItems);
          } else {
            params.successCallback([]);
          }
        },
      },
    };
  }

  private getDetailColumnDefs(): ColDef[] {
    return [
      {
        headerName: 'SKU',
        field: 'sku',
      },
      {
        headerName: 'Product Name',
        field: 'name',
      },
      {
        headerName: 'Quantity',
        field: 'quantity',
        valueFormatter: AgGridValueFormatter.DECIMAL_PLACES,
      },
      {
        headerName: 'Unit Price',
        field: 'unitPrice',
        valueFormatter: AgGridValueFormatter.CURRENCY,
      },
      {
        headerName: 'Total',
        valueGetter: (params) => {
          return params.data.quantity * params.data.unitPrice;
        },
        valueFormatter: AgGridValueFormatter.CURRENCY,
      },
    ];
  }

  private fetchRefundReturnOrders(): void {
    if (!this.parentSalesOrderId) {
      return;
    }

    this.loading = true;

    this.orderPostedApiService
      .getById({
        id: this.parentSalesOrderId ?? 0,
        params: {
          relations: [
            'refundOrders',
            'refundOrders.orderItems',
            'refundOrders.returnOrders',
            'refundOrders.returnOrders.orderItems',
            'returnOrders',
            'returnOrders.orderItems',
          ].join('|'),
          relationsWithColumns: 'true',
          excludeIgnoredOrderItems: 'true',
        },
      })
      .pipe(
        takeUntil(this.onDestroy$),
        tap({
          next: () => {
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        }),
      )
      .subscribe({
        next: (salesOrder) => {
          if (salesOrder) {
            const refundOrders = salesOrder.refundOrders || [];
            const returnOrders = salesOrder.returnOrders || [];

            // Flatten refundOrders and their nested returnOrders
            const mergedOrders = [];
            for (const refund of refundOrders) {
              mergedOrders.push({ ...refund, type: 'Refund Order' });
              if (Array.isArray(refund.returnOrders)) {
                for (const nestedReturn of refund.returnOrders) {
                  mergedOrders.push({
                    ...nestedReturn,
                    type: 'Return Order',
                    parentRefundOrderId: refund.id,
                  });
                }
              }
            }
            // Add top-level returnOrders
            for (const ret of returnOrders) {
              mergedOrders.push({ ...ret, type: 'Return Order' });
            }

            this.refundReturnOrders = mergedOrders;
            this.loadData();
            this.cd.detectChanges();
          }
        },
      });
  }

  private loadData(): void {
    if (this.gridApi) {
      this.gridApi.setRowData(this.refundReturnOrders);
    }
  }
}
